import {
  Box,
  Button,
  Divider,
  List,
  Typography,
  Grid,
} from '@material-ui/core';
import IntroPricingUpgradeTemplate from '../introPricingUpgradeTemplate';
import SubscriptionList from '../subscriptionList';
import { useRetrieveRetention } from '../../../common/hooks/useRetrieveRetention';
import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import LocaleKeys from '../../../locales/keys';
import { makeStyles } from '@material-ui/core/styles';
import { useGrantCoupon } from '../../../common/hooks/useGrantCoupon';
import { useRetrieveCoupon } from '../../../common/hooks/useRetrieveCoupon';
import { useUpdateSubscription } from '../../../common/hooks/useUpdateSubscription';
import { RetentionOfferMessage } from '../../components/retentionOfferMessage';
import { CancellationReasonForm } from '../../components/cancellationReasonForm';
import { UserSession } from 'ascendon-web-toolkit/utils';
import RenewSubscriptionReasonForm from '../renewSubscriptionReasonForm';
import events from '../../../common/events/events';
import postMessage, { postMessageOfferFlow } from '../../../utils/postMessage';
import useSubscriptionFlowAnalyticsParams from '../../../common/hooks/useSubscriptionFlowAnalyticsParams';
import { useCodeTable } from '../../../common/hooks/useCodeTable';
import ErrorScreen from '../../../common/errorScreen';
import { RetentionOfferDialog } from '../retentionOfferDialog';
import PostCancellationView from '../postCancellationView';
import AppliedOfferView from '../appliedOfferView';
import { getPaymentMethodDescriptionTextNode } from '../../helpers/subscriptionBasedProductCodeTables';
import sanitizeHtml from 'sanitize-html';
import { ELIGIBILITY_ACTION_EXTERNAL } from '../../../constants/upgradeJourney.constant';
import { ConfigMessageContext } from '../../../app/configMessageContext';
import CancellationConfirmationPopup from '../cancellationConfirmationPopup';
import {
  QUICK_CANCEL_CONFIRMATION_DISPLAYED,
  QUICK_CANCEL_CONFIRMATION_END,
} from '../../../constants/eventNames.constant';
import { quickCancelAnalyticEvents } from '../../helpers/quickCancelHelper';

const useStyles = makeStyles((theme) => ({
  topScrollPaper: {
    alignItems: 'flex-start',
  },
  topPaperScrollBody: {
    verticalAlign: 'top',
  },
  paper: {
    boxShadow: '0 5px 15px rgba(0,0,0,.5)',
    border: '1px solid rgba(0,0,0,.2)',
    borderRadius: '6px',
    marginLeft: 0,
    marginRight: 0,
  },
  dialogTitle: {
    whiteSpace: 'pre-line',
    textTransform: 'uppercase',
    padding: 0,
    marginRight: '40px',
  },
}));

const SubscriptionContainer = ({
  classes,
  subscriptionList,
  cancelSubscriptionLabel,
  subscriptionProps,
  subscriptionCycleDate,
  isFreeTrial,
  isAccountNumber,
  subId,
  isManagedPricingplan,
  onUpdate,
  imageUrl,
  statusName,
  planImageList,
  retrieveEligibilityUiModel,
  allSubscriptions,
}) => {
  const { t } = useTranslation();
  const { isQuickCancel, setIsQuickCancel } = useContext(ConfigMessageContext);
  const subscriptionContainerClasses = useStyles();

  const defaultCancellationReasonConfigValue =
    ASCENDON_ENV_CONFIG.retentionConfiguration?.defaultCancellationReason;
  const defaultCancellationIdentifier =
    ASCENDON_ENV_CONFIG.retentionConfiguration?.defaultCancellationIdentifier;
  const useRetentionOffer =
    ASCENDON_ENV_CONFIG.retentionConfiguration?.useRetentionOffer;

  const [retentionUiModel, retentionFault, requestRetention] =
    useRetrieveRetention();
  const [
    couponUiModel,
    grantCouponFault,
    grantFaultStatus,
    requestGrantCoupon,
  ] = useGrantCoupon();
  const [retrieveCouponModel, , requestRetrieveCoupon] = useRetrieveCoupon();

  const [
    updateSubscription,
    updateSubscriptionFault,
    requestUpdateSubscription,
    updateSubscriptionFaultStatus,
  ] = useUpdateSubscription();
  const [language, subscriptionType, subscriptionPackage] =
    useSubscriptionFlowAnalyticsParams();

  const [isCancellationReasonDisplay, setIsCancellationReasonDisplay] =
    useState(isQuickCancel);
  const [showRenewSubscriptionForm, setShowRenewSubscriptionForm] =
    useState(false);
  const [isPostCancellationFlow, setIsPostCancellationFlow] = useState(false);
  const [hasRetentionOffer, setHasRetentionOffer] = useState(undefined);
  const [disableCancelSubscription, setDisableCancelSubscription] =
    useState(useRetentionOffer);
  const [codeTable, codeTableFault, requestCodeTable] = useCodeTable();
  const [showErrorDialog, setShowErrorDialog] = useState(undefined);
  const [updateSubscriptionFaultErrorMsg, setUpdateSubscriptionFaultErrorMsg] =
    useState('');
  const [cancellationReasonFormErrorMsg, setCancellationReasonFormErrorMsg] =
    useState('');
  const [showRetentionOffer, setShowRetentionOffer] = useState(false);
  const [showPostCancellationView, setShowPostCancellationView] =
    useState(false);
  const [showAppliedOfferView, setShowAppliedOfferView] = useState(false);
  const [discountDetails, setDiscountDetails] = useState(undefined);
  const [showOfferMessage, setShowOfferMessage] = useState(false);
  const [
    isCancellationConfirmationPopupDisplay,
    setIsCancellationConfirmationPopupDisplay,
  ] = useState(false);
  const paymentMethodDescriptionTextNode =
    getPaymentMethodDescriptionTextNode(subscriptionProps);

  useEffect(() => {
    if (!codeTable) {
      requestCodeTable([150]);
    }
  }, []);

  useEffect(() => {
    if (useRetentionOffer && subId && subId > 0) {
      const subscriberId =
        UserSession?.sessionData?.SessionSummary?.SubscriberId ||
        UserSession?.sessionData?.Subscriber?.Id;

      requestRetention({
        newRequestData: { SubscriptionId: subId },
        headers: { subscriberId },
      });
    }
  }, [subId]);

  useEffect(() => {
    if (subscriptionProps) {
      setIsPostCancellationFlow(
        !subscriptionProps.isCancelable && subscriptionProps.renewable,
      );
    }
  }, [subscriptionProps]);

  const displayRetentionOfferHandler = (openCouponAfterCancellation) => {
    if (openCouponAfterCancellation) {
      setShowRetentionOffer(true);
    }
  };

  const cancelSubscriptionButtonHandler = () => {
    setCancellationReasonFormErrorMsg('');
    if (!subscriptionProps.isCancelable && subscriptionProps.renewable) {
      //RENEW SUBSCRIPTION
      setShowRenewSubscriptionForm((prevState) => !prevState);
    } else {
      if (hasRetentionOffer) {
        showLoader();
        const subscriberId =
          UserSession?.sessionData?.SessionSummary?.SubscriberId ||
          UserSession?.sessionData?.Subscriber?.Id;
        postMessageOfferFlow('', {
          ...events.cancelSubscription,
          subscriptionPackage,
          subscriptionType,
          language,
        });
        requestGrantCoupon({
          newRequestData: { SubscriptionId: subId },
          headers: { subscriberId },
        });
      } else {
        //not eligible for retention, display cancellation reason form for user to select
        postMessageOfferFlow('', {
          ...events.cancelSubscription,
          subscriptionPackage,
          subscriptionType,
          language,
        });
        setIsCancellationReasonDisplay(true);
      }
    }
  };

  const showLoader = () => {
    postMessage('module.showLoader');
  };

  const hideLoader = () => {
    postMessage('module.hideLoader');
  };

  useEffect(() => {
    if (couponUiModel) {
      setShowErrorDialog(false);
      if (hideLoader) {
        hideLoader();
      }
      setShowRetentionOffer(true);
    }
  }, [couponUiModel]);

  useEffect(() => {
    if (!retrieveCouponModel) {
      const subscriberId =
        UserSession?.sessionData?.SessionSummary?.SubscriberId ||
        UserSession?.sessionData?.Subscriber?.Id;
      requestRetrieveCoupon({
        headers: { subscriberId },
      });
    } else {
      if (
        retrieveCouponModel?.RedemptionCode &&
        !subscriptionProps.isCancelable
      ) {
        setShowOfferMessage(true);
      }
    }
  }, [retrieveCouponModel]);

  useEffect(() => {
    if (retentionUiModel) {
      if (isFreeTrial) {
        setHasRetentionOffer(false);
      } else {
        setHasRetentionOffer(retentionUiModel?.Eligible);
      }
    }
  }, [retentionUiModel]);

  useEffect(() => {
    if (hasRetentionOffer !== undefined) {
      setDisableCancelSubscription(false);
    }
  }, [hasRetentionOffer]);

  useEffect(() => {
    if (
      !showRenewSubscriptionForm &&
      !subscriptionProps.isCancelable &&
      subscriptionProps.renewable
    ) {
      setShowRenewSubscriptionForm((prevState) => !prevState);
    }
  }, [showRenewSubscriptionForm]);

  const handleCancellationReasonsClickClose = (showGAforDismissed = true) => {
    setIsCancellationReasonDisplay(false);
    if (showGAforDismissed) {
      postMessageOfferFlow('', {
        ...events.cancelComplete.dismissed,
        subscriptionPackage: subscriptionProps.currentPackage,
        subscriptionType,
        language,
      });
    }
  };

  const handleCancellationConfirmationPopupCloseHandler = () => {
    setIsCancellationConfirmationPopupDisplay(false);
    quickCancelAnalyticEvents(
      language,
      allSubscriptions,
      retrieveEligibilityUiModel?.EntitledPlans,
      isQuickCancel,
      QUICK_CANCEL_CONFIRMATION_END,
      subscriptionProps?.currentPlan,
    );
    setIsQuickCancel(false);
  };

  // Retention Offer
  const closeRetentionView = () => {
    setShowRetentionOffer(false);
  };

  const keepMySubscription = () => {
    setShowRetentionOffer(false);
  };

  const redeemedOffer = ({ discountDetails, updateSubscriptionResponse }) => {
    setShowRetentionOffer(false);
    setDiscountDetails(discountDetails);
    if (updateSubscriptionResponse) {
      updateModelAfterUpdate(updateSubscriptionResponse);
    }
    setShowOfferMessage(false);
  };

  useEffect(() => {
    if (discountDetails?.DiscountAmount) {
      postMessageOfferFlow('', {
        ...events.offerConfirmation.view,
        subscriptionPackage,
        subscriptionType,
        language,
      });
      setShowAppliedOfferView(true);
    }
  }, [discountDetails]);

  const cancelledSubscription = (terminationReason) => {
    postMessage('module.showLoader');
    setShowRetentionOffer(false);
    cancelSubscriptionAfterSeeingRetentionOffer(terminationReason);
    postMessageOfferFlow('', {
      ...events.cancelComplete.view,
      subscriptionPackage,
      subscriptionType,
      language,
    });
    setShowPostCancellationView(true);
  };

  const closePostCancellationView = () => {
    postMessageOfferFlow('', {
      ...events.cancelComplete.dismissed,
      subscriptionPackage,
      subscriptionType,
      language,
    });
    const subscriberId =
      UserSession?.sessionData?.SessionSummary?.SubscriberId ||
      UserSession?.sessionData?.Subscriber?.Id;
    requestRetrieveCoupon({
      headers: { subscriberId },
    });
    setShowPostCancellationView(false);
  };

  const returnToMySubscriptionPostCancellationView = () => {
    postMessageOfferFlow('', {
      ...events.cancelComplete.returnToMySubscription,
      subscriptionPackage,
      subscriptionType,
      language,
    });
    const subscriberId =
      UserSession?.sessionData?.SessionSummary?.SubscriberId ||
      UserSession?.sessionData?.Subscriber?.Id;
    requestRetrieveCoupon({
      headers: { subscriberId },
    });

    setShowPostCancellationView(false);
  };

  const closeAppliedOffer = () => {
    if (isPostCancellationFlow) {
      postMessageOfferFlow('', {
        ...events.offerConfirmation.returnToMySubscription,
        subscriptionPackage,
        subscriptionType,
        language,
      });
    } else {
      postMessageOfferFlow('', {
        ...events.offerConfirmation.dismissed,
        subscriptionPackage,
        subscriptionType,
        language,
      });
    }

    setShowAppliedOfferView(false);
  };

  const returnToMySubscriptionAppliedOffer = () => {
    postMessageOfferFlow('', {
      ...events.offerConfirmation.returnToMySubscription,
      subscriptionPackage,
      subscriptionType,
      language,
    });
    setShowAppliedOfferView(false);
  };

  const getDefaultCancellationValue = () => {
    if (codeTable) {
      const defaultCancellationCandiate = codeTable.Codes?.filter(
        (item) => item.Description === defaultCancellationIdentifier,
      );

      return (
        defaultCancellationCandiate[0]?.Value ||
        defaultCancellationReasonConfigValue
      );
    }
  };

  const cancelSubscriptionAfterSeeingRetentionOffer = (terminationReason) => {
    if (showRenewSubscriptionForm) {
      setShowRenewSubscriptionForm(false);
    }
    if (!isPostCancellationFlow) {
      const defaultCancellationValue = getDefaultCancellationValue();
      requestUpdateSubscription({
        Renew: false,
        TerminationReason:
          terminationReason?.Value ||
          defaultCancellationValue ||
          defaultCancellationReasonConfigValue,
        Id: subId,
      });
      postMessageOfferFlow('', {
        ...events.benefitsView.continueCancellation,
        subscriptionPackage,
        subscriptionType,
        language,
      });
    } else {
      postMessageOfferFlow('', {
        ...events.seeOffer.returnToMySubscription,
        subscriptionPackage,
        subscriptionType,
        language,
      });
    }
  };

  const updateModelAfterUpdate = (updatedSubscription) => {
    if (onUpdate) {
      onUpdate(updatedSubscription);
    }
  };

  const onUpdateConfirmationPopupHandler = () => {
    if (isQuickCancel) {
      setIsCancellationConfirmationPopupDisplay(true);
      quickCancelAnalyticEvents(
        language,
        allSubscriptions,
        retrieveEligibilityUiModel?.EntitledPlans,
        isQuickCancel,
        QUICK_CANCEL_CONFIRMATION_DISPLAYED,
        subscriptionProps?.currentPlan,
      );
    }
  };

  const handleCloseErrorDialog = (updateSubscriptionFault = '') => {
    postMessage('module.hideBackdrop');
    setShowErrorDialog(false);
    if (isCancellationReasonDisplay) {
      setIsCancellationReasonDisplay(false);
    } else if (showRenewSubscriptionForm) {
      setShowRenewSubscriptionForm(false);
    } else if (updateSubscriptionFault) {
      setCancellationReasonFormErrorMsg(updateSubscriptionFault);
      setShowErrorDialog(true);
      if (showRenewSubscriptionForm) {
        setShowRenewSubscriptionForm(false);
      }
    }
  };

  const showErrorDialogHandler = (fault, applyCouponFault) => {
    if (showRenewSubscriptionForm) {
      setShowRenewSubscriptionForm(false);
    }
    if (fault || applyCouponFault) {
      //setIsCouponModalOpen(false);
      setShowRetentionOffer(false);
    }
    if (showAppliedOfferView) {
      setShowAppliedOfferView(false);
    }
    if (showPostCancellationView) {
      setShowPostCancellationView(false);
    }
    setShowErrorDialog(true);
    setCancellationReasonFormErrorMsg(fault);
    if (applyCouponFault) {
      setCancellationReasonFormErrorMsg('');
    }
  };

  useEffect(() => {
    if (codeTable) {
      setShowErrorDialog(false);
    }
  }, [codeTable]);

  useEffect(() => {
    if (updateSubscription) {
      postMessageOfferFlow('', {
        ...events.cancelComplete.view,
        subscriptionPackage,
        subscriptionType,
        language,
      });
      updateModelAfterUpdate(updateSubscription);
      postMessage('module.hideLoader');
    }
  }, [updateSubscription]);

  useEffect(() => {
    if (showRetentionOffer) {
      if (isPostCancellationFlow) {
        postMessageOfferFlow('', {
          ...events.seeOffer.view,
          subscriptionPackage,
          subscriptionType,
          language,
        });
      } else {
        postMessageOfferFlow('', {
          ...events.benefitsView.view,
          subscriptionPackage,
          subscriptionType,
          language,
        });
      }
    }
  }, [showRetentionOffer]);

  useEffect(() => {
    if (retentionFault) {
      setHasRetentionOffer(false);
    }
  }, [retentionFault]);

  useEffect(() => {
    if (typeof grantFaultStatus === 'boolean' || grantCouponFault) {
      postMessage('module.hideLoader');
      setShowErrorDialog(true);
    }
  }, [grantFaultStatus]);

  useEffect(() => {
    if (
      updateSubscriptionFault &&
      typeof updateSubscriptionFaultStatus === 'boolean'
    ) {
      if (showPostCancellationView) {
        setShowPostCancellationView(false);
      }
      postMessage('module.hideLoader');
      setShowErrorDialog(true);
      setUpdateSubscriptionFaultErrorMsg(updateSubscriptionFault);
    }
  }, [updateSubscriptionFaultStatus, updateSubscriptionFault]);

  useEffect(() => {
    if (codeTableFault) {
      setShowErrorDialog(true);
    }
  }, [codeTableFault]);

  return showErrorDialog ? (
    <ErrorScreen
      errorDialog={true}
      subscriptionContainerClasses={subscriptionContainerClasses}
      dialogTitle={t(LocaleKeys.retention.errorDialogTitle)}
      dialogContent={
        updateSubscriptionFaultErrorMsg ||
        cancellationReasonFormErrorMsg ||
        t(LocaleKeys.retention.errorDialogDetail)
      }
      dialogActionButton={t(LocaleKeys.retention.errorDialogClose)}
      showErrorDialog={showErrorDialog}
      handleCloseErrorDialog={handleCloseErrorDialog}
      hideBackdrop={true}
    />
  ) : (
    <Box mb={5} className={classes.boxContainer}>
      <List
        component="nav"
        className={classes.nav}
        aria-label="subscription list"
      >
        {subscriptionProps.isAffiliateplan ? (
          <Box>
            <Typography component={'div'} className={classes.paymentHeader}>
              {t(LocaleKeys.subscriptionInfo.subscriptions.paymentHeader)}
            </Typography>
            <Typography component={'div'} className={classes.paymentText}>
              <Box mt={5} display="flex">
                <Typography variant="body1">
                  <span
                    dangerouslySetInnerHTML={{
                      __html: sanitizeHtml(
                        paymentMethodDescriptionTextNode.descriptionText,
                        {
                          allowedAttributes: {
                            a: ['style', 'href', 'target'],
                          },
                          transformTags: {
                            a: sanitizeHtml.simpleTransform('a', {
                              style: 'border-bottom: none; font-weight: bold;',
                            }),
                          },
                        },
                      ),
                    }}
                  />
                </Typography>
              </Box>
            </Typography>
          </Box>
        ) : null}

        {subscriptionProps.isIap ? (
          <Box>
            {subscriptionList.map((item) => (
              <SubscriptionList
                classes={classes}
                name={item.name}
                value={item.value}
                key={item.id}
              />
            ))}
            {!subscriptionProps.eligibilityAction?.includes(
              ELIGIBILITY_ACTION_EXTERNAL,
            ) && (
              <Box>
                <Typography variant="h3" className={classes.appStoreHeader}>
                  {t(LocaleKeys.subscriptionInfo.subscriptions.appStoreHeader)}
                </Typography>
                <Typography className={classes.appStoreText}>
                  {t(LocaleKeys.subscriptionInfo.subscriptions.appStoreText)}
                </Typography>
              </Box>
            )}
          </Box>
        ) : null}

        {!subscriptionProps.isIap && !subscriptionProps.isAffiliateplan ? (
          <Box>
            {subscriptionList.map((item) => (
              <SubscriptionList
                classes={classes}
                name={item.name}
                value={item.value}
                key={item.id}
              />
            ))}
          </Box>
        ) : null}

        {showRetentionOffer ? (
          <RetentionOfferDialog
            eligibleForRetention={hasRetentionOffer}
            open={true}
            grantCouponModel={
              couponUiModel ? couponUiModel : retrieveCouponModel
            }
            isPostCancellationFlow={isPostCancellationFlow}
            closeRetentionView={closeRetentionView}
            keepMySubscription={keepMySubscription}
            redeemedOffer={redeemedOffer}
            cancelledSubscription={cancelledSubscription}
            hideLoader={hideLoader}
            showErrorDialogHandler={showErrorDialogHandler}
            subscriptionProps={subscriptionProps}
            subId={subId}
          />
        ) : null}
        {showPostCancellationView ? (
          <PostCancellationView
            open={true}
            subscriptionProps={subscriptionProps}
            handleClose={closePostCancellationView}
            returnToMySubscription={returnToMySubscriptionPostCancellationView}
          />
        ) : null}
        {showAppliedOfferView ? (
          <AppliedOfferView
            open={true}
            subscriptionProps={subscriptionProps}
            discountDetails={discountDetails}
            handleClose={closeAppliedOffer}
            returnToMySubscription={returnToMySubscriptionAppliedOffer}
          />
        ) : null}

        <IntroPricingUpgradeTemplate
          subscriptionProps={subscriptionProps}
          subscriptionCycleDate={subscriptionCycleDate}
          isFreeTrial={isFreeTrial}
        />
        {!subscriptionProps.isIap && (
          <>
            {!subscriptionProps.isCancelable &&
            subscriptionProps.renewable &&
            !isManagedPricingplan &&
            showOfferMessage ? (
              <RetentionOfferMessage
                displayRetentionOfferHandler={displayRetentionOfferHandler}
                eligibleForRetention={hasRetentionOffer}
                couponModel={retrieveCouponModel}
              />
            ) : null}
            {!isManagedPricingplan &&
            !subscriptionProps.isIap &&
            !subscriptionProps.isAffiliateplan &&
            !showOfferMessage ? (
              <Grid container justifyContent="space-between">
                <Button
                  disableRipple
                  disableFocusRipple
                  size="small"
                  data-testid="cancel-subscription-label"
                  className={classes.baseButtonNode}
                  id={'cancelButtonClick'}
                  onClick={() => cancelSubscriptionButtonHandler()}
                  disabled={disableCancelSubscription}
                >
                  {!subscriptionProps.isCancelable ? (
                    <Typography className={classes.buttonNode}>
                      {t(
                        LocaleKeys.subscriptionInfo.subscriptions
                          .subscriptionItemRenewSubscriptionButtonLabel,
                      )}
                    </Typography>
                  ) : !subscriptionProps.isAffiliateplan ? (
                    <Typography className={classes.buttonNode}>
                      {cancelSubscriptionLabel}
                    </Typography>
                  ) : null}
                </Button>
                {isCancellationConfirmationPopupDisplay ? (
                  <CancellationConfirmationPopup
                    handleClose={
                      handleCancellationConfirmationPopupCloseHandler
                    }
                    open={true}
                    dialogTitle={t(
                      LocaleKeys.retention.cancellationConfirmationHeader,
                    )}
                    dialogContent={t(
                      LocaleKeys.retention.cancellationConfirmationContent,
                    )}
                    actionButtonCloseText={t(
                      LocaleKeys.retention.cancellationConfirmationClose,
                    )}
                  ></CancellationConfirmationPopup>
                ) : null}
                {isCancellationReasonDisplay ? (
                  <CancellationReasonForm
                    handleClose={handleCancellationReasonsClickClose}
                    open={true}
                    subId={subId}
                    onUpdate={updateModelAfterUpdate}
                    hasRetentionOffer={hasRetentionOffer}
                    cycleDate={subscriptionCycleDate}
                    codeTable={codeTable}
                    handleCloseErrorDialog={handleCloseErrorDialog}
                    currentPlan={subscriptionProps?.currentPlan}
                    imageUrl={imageUrl}
                    statusName={statusName}
                    planImageList={planImageList}
                    subscriptionList={subscriptionList}
                    subscriptionListClasses={classes}
                    isQuickCancel={isQuickCancel}
                    retrieveEligibilityUiModel={retrieveEligibilityUiModel}
                    allSubscriptions={allSubscriptions}
                    onUpdateConfirmationPopupHandler={
                      onUpdateConfirmationPopupHandler
                    }
                  ></CancellationReasonForm>
                ) : null}
                {showRenewSubscriptionForm &&
                !cancellationReasonFormErrorMsg ? (
                  <RenewSubscriptionReasonForm
                    showRenewSubscriptionForm={showRenewSubscriptionForm}
                    subscriptionCycleDate={subscriptionCycleDate}
                    subId={subId}
                    showErrorDialogHandler={showErrorDialogHandler}
                    onUpdate={updateModelAfterUpdate}
                    currentPlan={subscriptionProps?.currentPlan}
                  />
                ) : null}
              </Grid>
            ) : null}
            {isAccountNumber && <Divider />}
          </>
        )}
      </List>
    </Box>
  );
};

export default SubscriptionContainer;
